import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {WordAudioPipelineService} from "./word-audio.pipeline.service";
import {GlossaryPipelineService} from "./glossary.pipeline.service";
import {DictionaryPipelineService} from "./dictionary.pipeline.service";
import {VerifierPipelineService} from "./verifier.pipeline.service";
import {UtilitiesService} from "../utilities.service";
import {CssWord, CssSentence, CssSelection} from "../../content/content-view.objects";

import {
    ISelection,
    IExportGlossary,
    IExportDictionary,
    IDictionaryEntry,
    IGlossaryEntry,
    ITranslation,
    IText,
    ISentence,
    IWord,
    IQuestionEntry,
    IWordAudioTable,
    IAnswer, IVocabularyEntry
} from "../../api/data-model";
import {Scilearn} from "../../constants";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {flatMap} from "rxjs/operators";
import {ConversionPipelineService} from "./conversion.pipeline.service";

export interface IExpansions {
    [index: string]: Array<string>;

}

export enum ProcessingTask {
    QUESTION_IDS = <any>"question create ids",
    TEXT_RENUMBER = <any>"text renumber",
    APPLY_EXPANSIONS = <any>"apply expansions for numbers and abbreviations",
    DEFAULT = <any>"ad hoc processing task"
}

@Injectable()
export class BatchPipelineService {
    private vocabLists:Object;
    private errors:Object;
    private expansions:IExpansions;
    private missingPronunciations: Map<string, string[]>;
    private _pronunciationSelections:string;
    private audioRenameScript:string;
    private missingTAIList:string;
    private maxEntries:number;
    private maxLength:number;
    private maxQuestionId:number;
    private currentQuestionId:number;
    constructor(public http: HttpClient, private dictionaryService:DictionaryPipelineService,
                private wordAudioService:WordAudioPipelineService,
                private glossaryService:GlossaryPipelineService,
                private verifierPipelineService:VerifierPipelineService) {
        this.clearErrors();
        // TODO: this overlaps with what gets loaded in the component, maybe consolidate to there.
        this.glossaryService.loadGlossary().subscribe(glossaryResponse => {
            console.log("loaded glossary");
            /*const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
            this.http.get("resources/superuser/batchprocess/WordWallBatch3.txt",
            { headers, responseType: 'text'}).subscribe((response: any) => {
                let data = (response);
                this.vocabLists = this.processVocabData(data);
            });*/
            this.wordAudioService.loadWordAudioTable().subscribe((wordAudioResponse) => {
                const homographList = Object.keys(this.wordAudioService.audioTable)
                    .filter(key => key.indexOf("!") >= 0);
                this.dictionaryService.loadExpansions().subscribe((response: any) => {
                    this.expansions = (response as IExpansions);
                    Object.keys(this.expansions).forEach((key) => {
                        if (!this.expansions[key.toLowerCase()]) {
                            this.expansions[key.toLowerCase()] = this.expansions[key];
                        }
                    });
                });
            });
        });
    }

    public clearErrors(): void {
        this.missingPronunciations = new Map<string, string[]>();
        this._pronunciationSelections = "";
        this.audioRenameScript = "#!/bin/bash\n";
        this.missingTAIList = "";
        this.errors = {};
        this.maxEntries = 0;
        this.maxLength = 0;
    }

    public clearQuestionData(): void {
        this.maxQuestionId = 0;
        this.currentQuestionId = 0;
    }

    public initQuestionData(): void {
        this.currentQuestionId = this.maxQuestionId + 1;
        console.log("starting question numbers with:" + this.currentQuestionId);
    }

    private processVocabData(data: string): Object {
        let vocabLists = {};
        data = UtilitiesService.convertPunctuationToASCII(data);
        let lineData = data.split(/\n/).filter((l) => l !== "");
        lineData.forEach((line) => {
            console.log(line);
            let vocabData = line.split("\t");
            console.log(vocabData);
            let title = vocabData[0].toLowerCase();
            if (title !== "title") {
                title = title.replace(/:\s+chapter/, ", chapter");
                title = title.replace(/"/g, "");
                vocabData[1] = vocabData[1].replace(/\s*[(][^)]*[)]\s*/g, "");
                vocabLists[title] = vocabData[1].trim().replace(/"/g, "").split(/\s*,\s*/);
            }
        });
        console.log("vocabulary activity data " + JSON.stringify(vocabLists));
        return vocabLists;
    }

    public batchProcess(selection: ISelection, task: ProcessingTask): ISelection {
        let newSelection = JSON.parse(JSON.stringify(selection));
        this.errors[selection.metaData.fullTitle] = "";
        switch (task) {
            case ProcessingTask.TEXT_RENUMBER:
                this.batchTextRenumber(newSelection);
                break;
            case ProcessingTask.QUESTION_IDS:
                this.batchQuestionRenumber(newSelection);
                break;
            case ProcessingTask.APPLY_EXPANSIONS:
                this.batchAddExpansions(newSelection);
                break;
            case ProcessingTask.DEFAULT:
            default:
                this.batchTagGlossaryAsTarget(newSelection);
                // the default is a revolving door of whatever is needed at the time
                // selection.glossaryEntries = this.glossaryService.addGlossaryUsingV1Id(selection);
                // this.errors[selection.metaData.fullTitle] = "";
                /*newSelection.metaData.coverImage = newSelection.metaData.seriesName
                    ? newSelection.metaData.seriesName.replace(/[^a-zA-Z]/g, "")
                    : newSelection.metaData.referenceStory;
                newSelection.metaData.coverImage += ".jpg";*/
                // newSelection = this.fixVocabEntryDictionaries(newSelection);
                // newSelection = this.batchQuestionRenumber(newSelection);
                // this.findMissingTAIAnchors(newSelection);
                // this.addVocab(newSelection);
                // this.convertVocab(newSelection);
                // this.findMissingAnswerAudio(newSelection);
                break;
        }

        if (JSON.stringify(selection) === JSON.stringify(newSelection)) {
            return null;
        }
        return newSelection;

    }

    private findMissingTAIAnchors(newSelection: ISelection): void {
        let selectionTAIs = [];
        newSelection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        if (word.questionId && word.questionId.trim()) {
                            selectionTAIs.push(word.questionId.trim());
                        }
                    });
                });
            }
        });
        newSelection.questionEntries.forEach((question: IQuestionEntry) => {
            if (question.presentation === Scilearn.THINK_ABOUT_IT) {
                if (!selectionTAIs.includes(question.id)) {
                    this.missingTAIList +=
                        newSelection.metaData.fullTitle + "\t" + question.question.text.trim() + "\n";
                }
            }
        });
    }

    private findMissingAnswerAudio(newSelection: ISelection): void {
        newSelection.questionEntries.forEach((question: IQuestionEntry) => {
            if (question.type !== "OpenBook") {
                question.answers.forEach(answer => {
                    if (!answer.audio) {
                        console.log("missing answer audio in:" + newSelection.metaData.referenceStory + ":" +
                        JSON.stringify(answer, null, 2));
                    }
                });
            }
        });
    }

    private fixVocabEntryDictionaries(newSelection: ISelection): ISelection {
        console.log("processing word wall for: " + newSelection.metaData.referenceStory);
        if (newSelection.vocabularyEntries) {
            newSelection.vocabularyEntries.forEach((vocabEntry) => {
                let phrase = UtilitiesService.convertPunctuationToASCII(vocabEntry.word).trim().split(" ");
                console.log(phrase);
                if ((phrase.length > 1) || (vocabEntry.word !== phrase[0])) {
                    if (!vocabEntry.dictionary || vocabEntry.dictionary.length === 0) {
                        vocabEntry.dictionary = [phrase.join(" ")];
                    }
                }
            });
        }
        return newSelection;
    }

    public batchPrefixImages(newSelection: ISelection): ISelection {
        newSelection.text.forEach((text: IText) => {
            if (text.mediaFormat) {
                let prefix = newSelection.metaData.referenceStory + "-";
                if (text.mediaFormat.source !== "" && !text.mediaFormat.source.startsWith(prefix)) {
                    text.mediaFormat.source = prefix + text.mediaFormat.source;
                    console.log("new image source filename: " + text.mediaFormat.source);
                }
            }
        });
        return newSelection;
    }

    public batchAddExpansions(newSelection: ISelection): ISelection {
        newSelection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    let wordsWithoutDictionary = sentence.words.filter((word) => !word.dictionary);
                    wordsWithoutDictionary.forEach((word: IWord) => {
                        let term = word.word.toLowerCase();
                        if (this.expansions[term] && this.expansions[term].length > 0) {
                            console.log("found expansion for " + word.word + " in " +
                                        newSelection.metaData.referenceStory + ": " + this.expansions[term]);
                            // TODO: just choosing first homograph for now.  May want to add ability to review?
                            let firstExpansion = this.expansions[term][0];
                            word.dictionary = firstExpansion.indexOf("!") < 0 ? this.expansions[term] : [firstExpansion];
                            /*let newAudio = firstExpansion.toLowerCase().replace(/ /g, "_") + ".mp3";
                            if (word.audio) {
                                this.audioRenameScript += "mv " + word.audio.replace("-reencode", "") + " " +
                                                          newAudio + "\n";
                            }
                            word.audio = newAudio;*/
                            // this.wordAudioService.addWordToAudioTable(word);
                        } else if (UtilitiesService.convertPunctuationToASCII(word.word) !== word.word) {
                            word.dictionary = [UtilitiesService.convertPunctuationToASCII(word.word).toLowerCase()];
                        }
                    });
                });
            }
        });
        return newSelection;
    }

    public batchTextRenumber(newSelection: ISelection): ISelection {
        let wordId = 1;
        let sentenceId = 1;
        newSelection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.id = sentenceId.toString();
                    sentenceId++;
                    sentence.words.forEach((word: IWord) => {
                        word.id = wordId.toString();
                        wordId++;
                    });
                });
            }
        });
        newSelection.metaData.wordCount = `${wordId - 1}`;
        return newSelection;
    }

    public batchTagGlossaryAsTarget(newSelection: ISelection): ISelection {
        newSelection.text.forEach((text: IText) => {
            if (text.sentences) {
                text.sentences.forEach((sentence: ISentence) => {
                    sentence.words.forEach((word: IWord) => {
                        // TARGET category words are words that are treated more strictly in recognition because
                        // they are important or new vocabulary.  Anything that has glossary linked to it should
                        // be tagged as a target category word
                        if (word.glossary && word.category === Scilearn.WORD_CATEGORY_DEFAULT) {
                            word.category = Scilearn.WORD_CATEGORY_TARGET;
                        }
                    });
                });
            }
        });
        return newSelection;
    }

    public batchQuestionRenumber(newSelection: ISelection): ISelection {
        newSelection.questionEntries.forEach((question: IQuestionEntry) => {
            if (question.presentation === Scilearn.QUIZ_QUESTION && !question.id) {
                question.id = this.currentQuestionId.toString();
                this.currentQuestionId++;
            } else {
                if (ConversionPipelineService.UPCOMING_RELEASES.includes(newSelection.metaData.contentVersion) ||
                    newSelection.metaData.contentVersion === ConversionPipelineService.CONTENT_GROUP) {
                    // need to re-number all
                    if (question.presentation === Scilearn.QUIZ_QUESTION) {
                        question.id = this.currentQuestionId.toString();
                        this.currentQuestionId++;
                    } else {
                        let oldId = question.id;
                        console.log("need to replace TAI id " + oldId + " in text");
                        question.id = this.currentQuestionId.toString();
                        newSelection.text.forEach((text: IText) => {
                            if (text.sentences) {
                                text.sentences.forEach((sentence: ISentence) => {
                                    sentence.words.forEach((word: IWord) => {
                                        if (word.questionId) {
                                            console.log("found non-empty question id: " + word.questionId);
                                        }
                                        if (word.startQuestionId === oldId) {
                                            word.startQuestionId = question.id;
                                        }
                                        if (word.questionId === oldId) {
                                            word.questionId = question.id;
                                        }
                                    });
                                });
                            }
                        });
                        this.currentQuestionId++;
                    }
                }
            }
        });
        console.log("currentQuestionId after processing: " + newSelection.metaData.referenceStory + ":" +
                    this.currentQuestionId.toString());
        return newSelection;
    }

    public batchAnalyzePronunciations(selection: ISelection): void {
        let newSelection = JSON.parse(JSON.stringify(selection));
        let pronunciationDictionary =
            this.dictionaryService.addDictionary(newSelection);
        console.log("dict:" + JSON.stringify(pronunciationDictionary));
        Object.keys(pronunciationDictionary).forEach((word) => {
            if (!pronunciationDictionary[word]) {
                this.missingPronunciations.set(word, []);
            }
        });
        this._pronunciationSelections += selection.metaData.referenceStory + "_";
        console.log("missing prons:" + this.missingPronunciations.keys());
    }

    public alphabetizeMissingProns() {
        // recreate the map in alphabetized insertion order
        const words = Array.from(this.missingPronunciations.keys()).sort();
        this.missingPronunciations = new Map<string, string[]>();
        words.forEach(word => this.missingPronunciations.set(word, []));
    }

    public batchAnalyzeQuestionNumbers(selection: ISelection): void {
        let newSelection = JSON.parse(JSON.stringify(selection));
        newSelection.questionEntries.forEach(question => {
            let idNumber = Number(question.id);
            if (!Number.isNaN(idNumber) && idNumber > this.maxQuestionId) {
                this.maxQuestionId = idNumber;
            }
        });
    }

    public get pronunciationSelections(): string {
        return this._pronunciationSelections;
    }

    public get expansionsList(): IExpansions {
        return this.expansions;
    }

    public addExpansion(word: string, expansions: string[]) {
        if (!this.expansions[word]) {
            this.expansions[word] = expansions.map(expansion => expansion.toLowerCase());
            this.expansions[word.toLowerCase()] = expansions.map(expansion => expansion.toLowerCase());
        }
    }

    public addMissingWord(word: string): void {
        // first, make sure the word to be added isn't already on the list
        if (!this.missingPronunciations.get(word)) {
            this.missingPronunciations.set(word, []);
        }
    }

    public numMissingEntries(): number {
        return (this.missingPronunciations.size);
    }

    public getMissingWord(index: number): string {
        let list = Array.from(this.missingPronunciations.keys());
        if (list.length > 0 && list.length > index) {
            let word = list[index];
            // delete this.missingPronunciations[word]; move to splice
            return word;
        }
        return null;
    }

    public removeEntry(word: string): void {
        this.missingPronunciations.delete(word);
    }

    public batchVerify(selection: ISelection): Observable<string> {
        this.errors[selection.metaData.fullTitle] = "";
        console.log("starting verification for title: " + selection.metaData.fullTitle);
        let glossaryEntries: IExportGlossary = this.glossaryService.addGlossaryUsingV1Id(selection);
        let playToEnd = true;
        return this.verifierPipelineService.playReadToMeAudio(selection, playToEnd).pipe(flatMap((readToMeError) => {
            this.errors[selection.metaData.fullTitle] += readToMeError;
            this.verifierPipelineService.transitionAudioState();
            return this.verifierPipelineService.playWordAudio(selection, this.wordAudioService, playToEnd)
                .pipe(flatMap((wordAudioError) => {
                    this.errors[selection.metaData.fullTitle] += wordAudioError;
                    this.verifierPipelineService.transitionAudioState();
                    return this.verifierPipelineService.playGlossaryAudio(
                        selection, glossaryEntries, playToEnd).pipe(flatMap((glossaryError) => {
                        this.errors[selection.metaData.fullTitle] += glossaryError;
                        this.verifierPipelineService.transitionAudioState();
                        return this.verifierPipelineService.playQuestionAudio(
                            selection, playToEnd).pipe(flatMap((questionError) => {
                            this.errors[selection.metaData.fullTitle] += questionError;
                            this.verifierPipelineService.transitionAudioState();
                            // after all of the flatMapping the observable chain will generate 4 strings one for each
                            // of the audio types in the chain above.  We only want to act on the last of these
                            // after all audio has played
                            return ["", "", "", "DONE"];
                        }));
                    }));
                }));
        }));
    }

    private convertVocab(selection: ISelection): ISelection {
        if (selection.vocabularyEntries && selection.vocabularyEntries.length) {
            selection.vocabularyEntries.forEach((entry, index) => {
                // selection.vocabularyEntries[index] = this.convertVocabEntry(selection.metaData.fullTitle, entry);
                selection.vocabularyEntries[index] = this.fixVocabEntry(selection.metaData.fullTitle, entry);
            });
        } else {
            this.errors[selection.metaData.fullTitle] += "no vocab entries!";
        }
        return selection;
    }

    private fixVocabEntry(title: string, entry: IVocabularyEntry): IVocabularyEntry {
        // TODO: if entry.word is different from glossary term, replace entry.word with glossary term
        //       again if different, update entry.dictionary.  log any changes for review.
        //       log items needing pronunciations
        //       log longest entry, highest # of entries
        if (entry.glossary) {
            console.log("fixing term for: " + entry.glossary);
            let glossaryTerm = this.glossaryService.getEntryUsingV1Id(entry.glossary).entry.word;
            if (glossaryTerm !== entry.word) {
                if (glossaryTerm.toLowerCase() === entry.word.toLowerCase()) {
                    this.errors[title] +=
                        entry.word + " capitalization differs from glossary, auto-fixed to: " + glossaryTerm + "\n";
                    entry.word = glossaryTerm;
                } else {
                    this.errors[title] +=
                        entry.word + " term differs from glossary (changed): " + glossaryTerm + "\n";
                }
            }
            entry.word = glossaryTerm;
            let word = entry.word;
            if (word !== entry.word.trim()) {
                console.log("trimming white space from entry:" + entry.word + ":");
                entry.word = entry.word.trim();
            }
            word = UtilitiesService.convertPunctuationToASCII(word);
            let characteristics = UtilitiesService.getTextCharacteristics(word);
            if (characteristics) {
                this.errors[title] += word + " contains " + characteristics + "\n";
            }
            let wordArray = word.split(/[- ]/g);
            wordArray.forEach((wordInPhrase, index) => {
                let dictionaryEntry1 = this.dictionaryService.getWord(wordInPhrase + "!1");
                let dictionaryEntry2 = this.dictionaryService.getWord(wordInPhrase + "!2");
                let hasBoth = dictionaryEntry1.pronunciations && dictionaryEntry1.pronunciations.length &&
                              dictionaryEntry2.pronunciations && dictionaryEntry2.pronunciations.length;
                let has1st = dictionaryEntry1.pronunciations && dictionaryEntry1.pronunciations.length;
                let has2nd = dictionaryEntry2.pronunciations && dictionaryEntry2.pronunciations.length;
                if (has1st || has2nd) {
                    if (hasBoth) {
                        this.errors[title] += word + " contains ambiguous homograph (not fixed): "
                                              + wordArray[index] + "\n";
                    } else {
                        if (has1st) {
                            wordArray[index] = wordInPhrase + "!1";
                        }
                        if (has2nd) {
                            wordArray[index] = wordInPhrase + "!2";
                        }
                        this.errors[title] += word + " contains possible homograph (auto fixed): "
                                              + wordArray[index] + "\n";
                    }
                } else {
                    let dictionaryEntry = this.dictionaryService.getWord(wordInPhrase);
                    if (!dictionaryEntry.pronunciations || !dictionaryEntry.pronunciations.length) {
                        this.errors[title] += word + " missing pronunciation for:" + wordInPhrase + "\n";
                    }
                }
            });
            let finalWord = wordArray.join(" ").trim();
            if ((finalWord !== entry.word) || (wordArray.length > 1)) {
                if (entry.dictionary && entry.dictionary.length) {
                    if (finalWord !== entry.dictionary[0]) {
                        this.errors[title] += word + " has previous dictionary entry:"
                                              + entry.dictionary[0] + " NOT replaced with:" + finalWord + "\n";
                    }
                } else {
                    this.errors[title] += word + " has new dictionary entry:" + finalWord + "\n";
                    entry.dictionary = [finalWord];
                }
            }
        } else {
            this.errors[title] += entry.word + " missing glossary link" + "\n";
        }
        if (entry.word.length > 18) {
            this.errors[title] += entry.word + " is long entry of length " + entry.word.length.toString() + "\n";
        }
        if (entry.word.length > this.maxLength) {
            this.maxLength = entry.word.length;
        }
        return entry;
    }

    private convertVocabEntry(title: string, entry: IVocabularyEntry): IVocabularyEntry {
        if (entry.glossary) {
            let glossaryTerm = this.glossaryService.getEntryUsingV1Id(entry.glossary).entry.word;
            if (glossaryTerm !== entry.word) {
                if (glossaryTerm.toLowerCase() === entry.word.toLowerCase()) {
                    this.errors[title] +=
                        entry.word + " capitalization differs from glossary, auto-fixed to: " + glossaryTerm + "\n";
                    entry.word = glossaryTerm;
                } else {
                    this.errors[title] +=
                        entry.word + " term differs from glossary (not changed): " + glossaryTerm + "\n";
                }
            }
        }
        let word = entry.word;
        if (word !== entry.word.trim()) {
            console.log("trimming white space from entry:" + entry.word + ":");
            entry.word = entry.word.trim();
        }
        word = UtilitiesService.convertPunctuationToASCII(word);
        let characteristics = UtilitiesService.getTextCharacteristics(word);
        if (characteristics) {
            this.errors[title] += word + " contains " + characteristics + "\n";
        }
        let wordArray = word.split(/[- ]/g);
        wordArray.forEach((wordInPhrase, index) => {
            let dictionaryEntry = this.dictionaryService.getWord(wordInPhrase + "!1");
            if (dictionaryEntry.pronunciations && dictionaryEntry.pronunciations.length) {
                wordArray[index] = wordInPhrase + "!1";
                this.errors[title] += word + " contains possible homograph (auto fixed): " + wordArray[index] + "\n";
            }
        });
        let finalWord = wordArray.join(" ").trim();
        if ((finalWord !== entry.word) || (wordArray.length > 1)) {
            console.log("adding dictionary entry for word:" + entry.word + ":");
            entry.dictionary = [finalWord];
        }
        return entry;
    }

    private addVocab(selection: ISelection): ISelection {
        console.log("adding vocab for: " + selection.metaData.referenceStory);
        if (selection.metaData.newContent) { // only doing this for new content going forward
            selection.glossaryEntries = this.glossaryService.addGlossaryUsingV1Id(selection);
            console.log(JSON.stringify(selection.glossaryEntries));
            this.addVocabularyEntries(selection);
            delete selection.glossaryEntries;
        }
        return selection;
    }

    private addMetadata(selection: ISelection):ISelection {
        if (!selection.metaData.newContent && selection.metaData.referenceStory.endsWith("_607")) {
            // tslint:disable-next-line:no-string-literal
            if (!selection.metaData["duplicateK3Selection"]) {
                selection.metaData.contentVersion = "0";
            } else {
                selection.metaData.contentVersion = "UNPUBLISHED";
            }
            return selection;
        } else {
            return null;
        }
    }

    private addVocabularyEntries(selection: ISelection): void {
        let cssSelection = new CssSelection(selection);
        let selectionTitle = UtilitiesService.convertPunctuationToASCII(
            selection.metaData.fullTitle.toLowerCase());
        let vocabTerms = this.vocabLists[selectionTitle];
        if (!vocabTerms) {
            vocabTerms = this.vocabLists[selectionTitle.replace(/^the /, "")];
        }
        if (vocabTerms) {
            selection.vocabularyEntries = this.findVocabularyEntries(cssSelection, vocabTerms);
        } else {
            alert("no vocab terms for: " + selection.metaData.fullTitle);
            this.errors[cssSelection.metaData.fullTitle] += "no glossary found for this selection";
        }
    }

    private findVocabularyEntries(cssSelection: CssSelection, vocabTerms:string[]): IVocabularyEntry[] {
        let vocabularyEntries:IVocabularyEntry[] = [];
        vocabTerms.forEach((term) => {
            vocabularyEntries.push(this.findVocabularyEntry(cssSelection, term));
        });
        return vocabularyEntries.filter((entry) => entry);
    }

    private findVocabularyEntry(cssSelection: CssSelection, vocabTerm:string): IVocabularyEntry {
        let vocabularyEntry = this.getSelectionExactMatch(cssSelection, vocabTerm.toLowerCase());
        if (!vocabularyEntry) {
            vocabularyEntry = this.getSelectionNearMatch(cssSelection, vocabTerm.toLowerCase());
        }
        if (!vocabularyEntry) {
            vocabularyEntry = this.getGlobalMatch(cssSelection.metaData.fullTitle, vocabTerm);
        }
        if (!vocabularyEntry) {
            this.errors[cssSelection.metaData.fullTitle] += "no glossary found for " + vocabTerm + "\n";
            vocabularyEntry = {word: vocabTerm, glossary: null, translations: null};
        }
        return vocabularyEntry;
    }

    private getGlobalMatch(title: string, vocabTerm:string):IVocabularyEntry {
        // TODO: may still have case issues here
        let vocabularyEntry: IVocabularyEntry = null;
        let entries = [];
        let matchedForm = "";
        if (this.glossaryService.glossary[vocabTerm]) {
            // if >1 definition in the glossary, mark as needing review
            entries = this.glossaryService.glossary[vocabTerm].entries;
        } else {
            let forms: string[] = [vocabTerm + "s", vocabTerm.replace(/e$/, "") + "ed",
                                   vocabTerm + "'s", vocabTerm.replace(/e$/, "") + "ing",
                                   vocabTerm.replace(/ed$/, "e"), vocabTerm.replace(/ed$/, "")];
            forms.forEach((form) => {
                if (this.glossaryService.glossary[form]) {
                    entries = this.glossaryService.glossary[form].entries;
                    matchedForm = "(" + form + ")";
                }
            });
        }
        if (entries.length > 1) {
            this.errors[title] += "multiple global glossary for "
                                            + vocabTerm + matchedForm
                                            + " using first entry:" + entries[0].id + "\n";
        }
        if (entries.length) {
            let trans: ITranslation[] = entries[0].translations.filter((t) => t.language === "es");
            if (trans.length > 1) {
                this.errors[title] += "multiple global translations for "
                                                    + vocabTerm + matchedForm + " using first entry:"
                                                    + trans[0].id + "\n";
            }
            vocabularyEntry = {
                word: vocabTerm,
                glossary: entries[0].id,
                translations: {"es": trans[0].id}
            };
        }
        return vocabularyEntry;
    }

    private getSelectionExactMatch(cssSelection: CssSelection, vocabTerm: string): IVocabularyEntry {
        let vocabularyEntry:IVocabularyEntry = null;
        cssSelection.text.forEach((text) => {
            if (text.sentences) {
                text.sentences.forEach((sentence) => {
                    sentence.words.filter((w) => w.glossary).forEach((word) => {
                        let selectionTerm = this.glossaryService.getGlossaryTerm(word, sentence).toLowerCase();
                        console.log("selectionTerm: " + selectionTerm + " glossary id: " + word.glossary);
                        let glossaryTerm = cssSelection.glossaryEntries[word.glossary].word;
                        glossaryTerm = UtilitiesService.convertPunctuationToASCII(glossaryTerm).toLowerCase();
                        if ((glossaryTerm === vocabTerm) || (selectionTerm === vocabTerm)) {
                            if (vocabularyEntry && ((vocabularyEntry.glossary !== word.glossary) ||
                                                    (JSON.stringify(vocabularyEntry.translations) !== JSON.stringify(
                                                        word.translations)))) {
                                let reason: string;
                                if (vocabularyEntry.glossary !== word.glossary) {
                                    reason = "multiple selection glossary entries for " + vocabTerm + ":"
                                             + [vocabularyEntry.glossary, word.glossary].join(",");
                                } else {
                                    reason = "multiple selection translations for " + vocabTerm + ":"
                                             + [vocabularyEntry.translations, word.translations].join(",");
                                }
                                this.errors[cssSelection.metaData.fullTitle] += reason;
                            }
                            vocabularyEntry = {
                                word: vocabTerm, glossary: word.glossary, translations: word.translations
                            };
                        }
                    });
                });
            }
        });
        return vocabularyEntry;
    }

    private getSelectionNearMatch(cssSelection: CssSelection, vocabTerm: string): IVocabularyEntry {
        let vocabularyEntry:IVocabularyEntry = null;
        cssSelection.text.forEach((text) => {
            if (text.sentences) {
                text.sentences.forEach((sentence) => {
                    sentence.words.filter((w) => w.glossary).forEach((word) => {
                        let glossaryTerm = cssSelection.glossaryEntries[word.glossary].word;
                        glossaryTerm = UtilitiesService.convertPunctuationToASCII(glossaryTerm).toLowerCase();
                        if (this.nearMatch(glossaryTerm, vocabTerm)) {
                            vocabularyEntry = {
                                word: vocabTerm, glossary: word.glossary, translations: word.translations
                            };
                            this.errors[cssSelection.metaData.fullTitle] += "near match " + glossaryTerm +
                                                                                      " to " + vocabTerm + "\n";
                        }
                    });
                });
            }
        });
        return vocabularyEntry;
    }

    private nearMatch(glossaryTerm: string, vocabTerm: string): boolean {
        let baseTerm, morphTerm;
        if (glossaryTerm.length < vocabTerm.length) {
            baseTerm = glossaryTerm;
            morphTerm = vocabTerm;
        } else {
            baseTerm = vocabTerm;
            morphTerm = glossaryTerm;
        }
        let morphTermBase:string;
        if (morphTerm.match(/(ed|ing)$/)) {
            morphTermBase = morphTerm.replace(/(ed|ing)$/, "");
            if ((morphTermBase === baseTerm) || (morphTermBase + "e" === baseTerm)) {
                return true;
            }
        }
        morphTermBase = morphTerm.replace(/(s|'s)$/, "");
        if (morphTermBase === baseTerm) {
            return true;
        }
        return false;
    }

    public downloadErrors(): void {
        let errorString = "";
        Object.keys(this.errors).forEach((title) => {
            if (this.errors[title]) {
                errorString += title + ":\n" + this.errors[title] + "\n";
            }
        });
        console.log("error string: " + errorString);
        if (errorString) {
            UtilitiesService.downloadFile(errorString, "errors.txt");
        }
        // UtilitiesService.downloadFile(this.missingTAIList, "missingTAIs.txt");
        // UtilitiesService.downloadFile(this.audioRenameScript, "audioRename.sh");
        // UtilitiesService.downloadFile(JSON.stringify(this.wordAudioService.audioTable, null, "\t"),
                                      // "wordAudioTable.json");
    }
}
